import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useQuery } from "@tanstack/react-query";
import { axiosConfig } from "../../axios/axiosConfig";
import BasketTable from "./BasketTable";
import styles from "./BasketManagement.module.css";

const BASKET_API_URL = "/predictions/basket/";
const BASKET_CHOICES_API_URL = "/predictions/basket/choices/";
const PLANS_API_URL = "/payment/plans";

const DEFAULT_DISCLAIMER =
  "Investment in securities market are subject to market risks. Read all the related documents carefully before investing.";
const DEFAULT_TERMS =
  "This basket is a research recommendation only. Orders are placed by user through own broker.";

const NUMERIC_FIELDS = ["weight", "quantity", "target_price", "stop_loss"];

const sanitizeNumeric = (value) =>
  value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");

const emptyStock = () => ({
  symbol: "",
  weight: "",
  quantity: "",
  target_price: "",
  stop_loss: "",
  rationale: "",
});

const emptyForm = () => ({
  name: "",
  description: "",
  strategy: "",
  holding_period: "",
  benchmark: "",
  risk_level: "",
  investment_thesis: "",
  why_this_basket: "",
  sector_outlook: "",
  risk_description: "",
  disclaimer: DEFAULT_DISCLAIMER,
  terms_of_use: DEFAULT_TERMS,
  guidelines: "",
  review_frequency: "",
  status: "DRAFT",
  is_free: false,
  selected_plan_ids: [],
  stocks: [emptyStock()],
});

const useBasketChoices = () =>
  useQuery({
    queryKey: ["basket-choices"],
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    queryFn: async () => {
      const res = await axiosConfig({
        method: "get",
        url: BASKET_CHOICES_API_URL,
      });
      return res?.data?.response || {};
    },
  });

const PlansSelector = ({ selectedPlanIds, setSelectedPlanIds }) => {
  const { data: plansData = [] } = useQuery({
    queryKey: ["basket-plans-list"],
    retry: false,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await axiosConfig({ method: "get", url: PLANS_API_URL });
      return (
        res?.data?.response?.data || res?.data?.response || res?.data || []
      );
    },
    onError: (error) => {
      toast.error(error?.response?.data?.response || "Failed to fetch plans");
    },
  });

  const toggle = (id) => {
    setSelectedPlanIds((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const isAllSelected =
    plansData.length > 0 && selectedPlanIds.length === plansData.length;

  const onSelectAllChange = (e) => {
    setSelectedPlanIds(e.target.checked ? plansData.map((p) => p.id) : []);
  };

  return (
    <>
      <label className={styles.selectAllRow}>
        <input
          type="checkbox"
          checked={isAllSelected}
          onChange={onSelectAllChange}
        />
        SELECT ALL
      </label>

      <div className={styles.plansGrid}>
        {plansData.map((plan) => (
          <label
            key={plan.id}
            className={`${styles.planItem} ${
              selectedPlanIds.includes(plan.id) ? styles.planItemChecked : ""
            }`}
          >
            <input
              type="checkbox"
              checked={selectedPlanIds.includes(plan.id)}
              onChange={() => toggle(plan.id)}
            />
            {plan.name} {plan.price != null ? `(₹${plan.price})` : ""}
          </label>
        ))}
        {plansData.length === 0 && (
          <Typography sx={{ fontSize: "12px", color: "#94a3b8" }}>
            No plans found
          </Typography>
        )}
      </div>
    </>
  );
};

const BasketForm = ({
  form,
  setForm,
  choices,
  isEditing,
  submitting,
  onSubmit,
  onClear,
  onCancel,
}) => {
  const updateField = (field, value) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const formInputOnChange = (event) => {
    const { name, value } = event.target;
    updateField(name, value);
  };

  const updateStock = (idx, field, rawValue) => {
    const value = NUMERIC_FIELDS.includes(field)
      ? sanitizeNumeric(rawValue)
      : rawValue;
    setForm((prev) => {
      const stocks = [...prev.stocks];
      stocks[idx] = { ...stocks[idx], [field]: value };
      return { ...prev, stocks };
    });
  };

  const addStock = () =>
    setForm((prev) => ({ ...prev, stocks: [...prev.stocks, emptyStock()] }));

  const removeStock = (idx) =>
    setForm((prev) => ({
      ...prev,
      stocks: prev.stocks.filter((_, i) => i !== idx),
    }));

  return (
    <div className={styles.outerFormWrapper}>
      <div className={styles.formWrapper}>
        <h2>{isEditing ? "Edit Basket" : "Basket Form"}</h2>

        <div className={styles.formBody}>
          <div className={styles.inputControlBox}>
            <p className={styles.label}>Basket Name</p>
            <input
              type="text"
              name="name"
              value={form.name}
              onChange={formInputOnChange}
              placeholder="e.g. Defence Growth Basket"
            />
          </div>

          <div className={`${styles.inputControlBox} ${styles.stacked}`}>
            <p className={styles.label}>Description</p>
            <textarea
              rows={2}
              className={styles.wideTextarea}
              name="description"
              value={form.description}
              onChange={formInputOnChange}
              placeholder="Short description of the basket"
            />
          </div>

          <div className={styles.pairRow}>
            <div className={styles.inputControlBox}>
              <p className={styles.label}>Strategy</p>
              <select
                className={styles.selectedSymbolInput}
                name="strategy"
                value={form.strategy}
                onChange={formInputOnChange}
              >
                <option value="" disabled>
                  -- Select Strategy --
                </option>
                {(choices.strategy || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.inputControlBox}>
              <p className={styles.label}>Holding Period</p>
              <select
                className={styles.selectedSymbolInput}
                name="holding_period"
                value={form.holding_period}
                onChange={formInputOnChange}
              >
                <option value="" disabled>
                  -- Select Holding Period --
                </option>
                {(choices.holding_period || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.pairRow}>
            <div className={styles.inputControlBox}>
              <p className={styles.label}>Benchmark</p>
              <input
                type="text"
                name="benchmark"
                value={form.benchmark}
                onChange={formInputOnChange}
                placeholder="e.g. NIFTY 50"
              />
            </div>

            <div className={styles.inputControlBox}>
              <p className={styles.label}>Risk Level</p>
              <select
                className={styles.selectedSymbolInput}
                name="risk_level"
                value={form.risk_level}
                onChange={formInputOnChange}
              >
                <option value="" disabled>
                  -- Select Risk Level --
                </option>
                {(choices.risk_level || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className={styles.pairRow}>
            <div className={styles.inputControlBox}>
              <p className={styles.label}>Review Frequency</p>
              <select
                className={styles.selectedSymbolInput}
                name="review_frequency"
                value={form.review_frequency}
                onChange={formInputOnChange}
              >
                <option value="" disabled>
                  -- Select Review Frequency --
                </option>
                {(choices.review_frequency || []).map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.inputControlBox}>
              <p className={styles.label}>Status</p>
              <select
                className={styles.selectedSymbolInput}
                name="status"
                value={form.status}
                onChange={formInputOnChange}
              >
                {(choices.status || [])
                  .filter((opt) => opt.value !== "CLOSED")
                  .map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <p className={styles.sectionTitle}>Narrative</p>

          <div className={styles.pairRow}>
            {[
              ["investment_thesis", "Investment Thesis"],
              ["why_this_basket", "Why This Basket"],
            ].map(([field, label]) => (
              <div
                key={field}
                className={`${styles.inputControlBox} ${styles.stacked}`}
              >
                <p className={styles.label}>{label}</p>
                <textarea
                  rows={2}
                  className={styles.wideTextarea}
                  name={field}
                  value={form[field]}
                  onChange={formInputOnChange}
                />
              </div>
            ))}
          </div>

          <div className={styles.pairRow}>
            {[
              ["sector_outlook", "Sector Outlook"],
              ["risk_description", "Risk Description"],
            ].map(([field, label]) => (
              <div
                key={field}
                className={`${styles.inputControlBox} ${styles.stacked}`}
              >
                <p className={styles.label}>{label}</p>
                <textarea
                  rows={2}
                  className={styles.wideTextarea}
                  name={field}
                  value={form[field]}
                  onChange={formInputOnChange}
                />
              </div>
            ))}
          </div>

          <p className={styles.sectionTitle}>Legal & Guidelines</p>

          <div className={styles.pairRow}>
            {[
              ["disclaimer", "Disclaimer"],
              ["terms_of_use", "Terms of Use"],
            ].map(([field, label]) => (
              <div
                key={field}
                className={`${styles.inputControlBox} ${styles.stacked}`}
              >
                <p className={styles.label}>{label}</p>
                <textarea
                  rows={2}
                  className={styles.wideTextarea}
                  name={field}
                  value={form[field]}
                  onChange={formInputOnChange}
                />
              </div>
            ))}
          </div>

          <div className={`${styles.inputControlBox} ${styles.stacked}`}>
            <p className={styles.label}>Guidelines</p>
            <textarea
              rows={2}
              className={styles.wideTextarea}
              name="guidelines"
              value={form.guidelines}
              onChange={formInputOnChange}
            />
          </div>

          <p className={styles.sectionTitle}>Stocks</p>

          {form.stocks.map((stock, idx) => (
            <div key={idx} className={styles.stockCard}>
              <div className={styles.stockRow}>
                <input
                  placeholder="Symbol"
                  value={stock.symbol}
                  onChange={(e) =>
                    updateStock(idx, "symbol", e.target.value.toUpperCase())
                  }
                />
                <input
                  placeholder="Weight %"
                  value={stock.weight}
                  onChange={(e) => updateStock(idx, "weight", e.target.value)}
                />
                <input
                  placeholder="Quantity"
                  value={stock.quantity}
                  onChange={(e) => updateStock(idx, "quantity", e.target.value)}
                />
                {!isEditing && (
                  <button
                    type="button"
                    className={styles.removeStockBtn}
                    onClick={() => removeStock(idx)}
                    disabled={form.stocks.length === 1}
                  >
                    ×
                  </button>
                )}
              </div>
              <div className={styles.stockRow}>
                <input
                  placeholder="Target Price"
                  value={stock.target_price}
                  onChange={(e) =>
                    updateStock(idx, "target_price", e.target.value)
                  }
                />
                <input
                  placeholder="Stop Loss"
                  value={stock.stop_loss}
                  onChange={(e) =>
                    updateStock(idx, "stop_loss", e.target.value)
                  }
                />
                <input
                  placeholder="Rationale (optional)"
                  value={stock.rationale}
                  onChange={(e) =>
                    updateStock(idx, "rationale", e.target.value)
                  }
                />
              </div>
            </div>
          ))}

          <button
            type="button"
            className={styles.addStockBtn}
            onClick={addStock}
          >
            + Add Stock
          </button>

          <div className={styles.btnContainer}>
            <button
              type="button"
              className={styles.submitBtn}
              onClick={onSubmit}
              disabled={submitting}
            >
              {submitting
                ? isEditing
                  ? "Saving..."
                  : "Creating..."
                : isEditing
                  ? "Update Basket"
                  : "Create Basket"}
            </button>
            <button type="button" className={styles.clearBtn} onClick={onClear}>
              Clear
            </button>
            <button
              type="button"
              className={styles.cancelEditBtn}
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div className={styles.plansPanel}>
        <h2>Plan & Visibility</h2>
        <div className={styles.plansPanelBody}>
          <PlansSelector
            selectedPlanIds={form.selected_plan_ids}
            setSelectedPlanIds={(updater) =>
              setForm((prev) => ({
                ...prev,
                selected_plan_ids:
                  typeof updater === "function"
                    ? updater(prev.selected_plan_ids)
                    : updater,
              }))
            }
          />
        </div>
      </div>
    </div>
  );
};

const buildPayload = (form) => ({
  name: form.name,
  description: form.description,
  strategy: form.strategy,
  holding_period: form.holding_period,
  benchmark: form.benchmark,
  risk_level: form.risk_level,
  investment_thesis: form.investment_thesis,
  why_this_basket: form.why_this_basket,
  sector_outlook: form.sector_outlook,
  risk_description: form.risk_description,
  disclaimer: form.disclaimer,
  terms_of_use: form.terms_of_use,
  guidelines: form.guidelines,
  review_frequency: form.review_frequency,
  status: form.status,
  is_free: false,
  visible_to: (form.selected_plan_ids || []).join(","),
  stocks: form.stocks
    .filter((s) => s.symbol.trim())
    .map((s) => ({
      symbol: s.symbol.trim(),
      weight: s.weight ? Number(s.weight) : 0,
      quantity: s.quantity ? Number(s.quantity) : 0,
      ...(s.target_price ? { target_price: Number(s.target_price) } : {}),
      ...(s.stop_loss ? { stop_loss: Number(s.stop_loss) } : {}),
      ...(s.rationale.trim() ? { rationale: s.rationale.trim() } : {}),
    })),
});

const BasketManagement = () => {
  const { data: choices = {} } = useBasketChoices();

  const [baskets, setBaskets] = useState([]);
  const [tableLoading, setTableLoading] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyForm());
  const [creating, setCreating] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm());
  const [editBasket, setEditBasket] = useState(null);
  const [editing, setEditing] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(null);

  const fetchBaskets = async () => {
    setTableLoading(true);
    try {
      const res = await axiosConfig({ method: "get", url: BASKET_API_URL });
      setBaskets(
        res?.data?.response?.data || res?.data?.response || res?.data || []
      );
    } catch (err) {
      toast.error(
        err?.response?.data?.response ||
          err.message ||
          "Failed to fetch baskets",
      );
    } finally {
      setTableLoading(false);
    }
  };

  useEffect(() => {
    fetchBaskets();
  }, []);

  const closeCreate = () => {
    if (!creating) {
      setCreateOpen(false);
      setCreateForm(emptyForm());
    }
  };

  const handleCreate = async () => {
    if (!createForm.name.trim()) return toast.error("Basket name is required");
    if (!createForm.stocks.some((s) => s.symbol.trim()))
      return toast.error("Add at least one stock");
    setCreating(true);
    try {
      await axiosConfig({
        method: "post",
        url: BASKET_API_URL,
        data: buildPayload(createForm),
      });
      toast.success("Basket created");
      closeCreate();
      fetchBaskets();
    } catch (err) {
      toast.error(
        err?.response?.data?.response ||
          err.message ||
          "Failed to create basket",
      );
    } finally {
      setCreating(false);
    }
  };

  const fetchBasketDetail = async (basketId) => {
    const res = await axiosConfig({
      method: "get",
      url: `${BASKET_API_URL}${basketId}/`,
    });
    return res?.data?.response?.data || res?.data?.response;
  };

  const openEdit = async (basket) => {
    setLoadingDetail(basket.id);
    try {
      const detail = (await fetchBasketDetail(basket.id)) || basket;
      setEditBasket(detail);
      setEditForm({
        ...emptyForm(),
        ...detail,
        selected_plan_ids: (detail.visible_to || []).map((p) => p.id),
        stocks: detail.holdings?.length
          ? detail.holdings.map((h) => ({
              symbol: h.symbol || "",
              weight: h.weight ?? "",
              quantity: h.quantity ?? "",
              target_price: h.target_price ?? "",
              stop_loss: h.stop_loss ?? "",
              rationale: h.rationale || "",
            }))
          : [emptyStock()],
      });
      setEditOpen(true);
    } catch (err) {
      toast.error(
        err?.response?.data?.response ||
          err.message ||
          "Failed to load basket details",
      );
    } finally {
      setLoadingDetail(null);
    }
  };

  const closeEdit = () => {
    if (!editing) {
      setEditOpen(false);
      setEditBasket(null);
    }
  };

  const handleEdit = async () => {
    if (!editForm.name.trim()) return toast.error("Basket name is required");
    setEditing(true);
    try {
      await axiosConfig({
        method: "put",
        url: `${BASKET_API_URL}${editBasket.id}/`,
        data: buildPayload(editForm),
      });
      toast.success("Basket updated");
      closeEdit();
      fetchBaskets();
    } catch (err) {
      toast.error(
        err?.response?.data?.response ||
          err.message ||
          "Failed to update basket",
      );
    } finally {
      setEditing(false);
    }
  };

  const handleStatusAction = async (basket, action, close_reason) => {
    try {
      await axiosConfig({
        method: "post",
        url: `${BASKET_API_URL}${basket.id}/status/`,
        data: action === "CLOSE" ? { action, close_reason } : { action },
      });
      toast.success(`Basket status updated to ${action}`);
      fetchBaskets();
    } catch (err) {
      toast.error(
        err?.response?.data?.response ||
          err.message ||
          "Failed to update basket status",
      );
    }
  };

  // Manual close: exit every holding at its chosen price, then close the basket
  const handleManualClose = async (basket, exitPrices, close_reason, shouldClose) => {
    try {
      if (shouldClose) {
        // Exiting every holding: one bulk call closes the basket too
        await axiosConfig({
          method: "post",
          url: `${BASKET_API_URL}manual-exit/`,
          data: { basket_id: basket.id, close_reason, stocks: exitPrices },
        });
        toast.success("Basket closed");
      } else {
        // Partial exit: one call per stock, basket stays open
        for (const { symbol, exit_price } of exitPrices) {
          const res = await axiosConfig({
            method: "post",
            url: `${BASKET_API_URL}${basket.id}/exit-stock/`,
            data: { symbol, exit_price },
          });
          toast.success(res?.data?.response?.message || `${symbol} exited`);
        }
      }
      fetchBaskets();
    } catch (err) {
      toast.error(
        err?.response?.data?.response ||
          err.message ||
          "Failed to update basket",
      );
    }
  };

  return (
    <>
      <Box sx={{ p: 3, background: "#eef2f7", minHeight: "100vh" }}>
        <Box
          sx={{
            background: "#04343a",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
            p: "20px 28px",
            mb: 3,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <ShoppingBasketIcon sx={{ color: "#fff", fontSize: 32 }} />
            <Box>
              <Typography
                sx={{ color: "#fff", fontWeight: 700, fontSize: "20px" }}
              >
                Basket
              </Typography>
              <Typography
                sx={{ color: "rgba(255,255,255,0.65)", fontSize: "13px" }}
              >
                Create and manage stock baskets
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateOpen(true)}
            sx={{
              background: "#016551",
              borderRadius: "8px",
              fontWeight: 600,
              px: 3,
              boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
              "&:hover": {
                background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)",
                boxShadow: "0 6px 16px rgba(1,101,81,0.4)",
              },
            }}
          >
            Create Basket
          </Button>
        </Box>

        <BasketTable
          baskets={baskets}
          loading={tableLoading}
          loadingDetailId={loadingDetail}
          onEdit={openEdit}
          onStatusAction={handleStatusAction}
          onFetchBasketDetail={fetchBasketDetail}
          onManualClose={handleManualClose}
        />
      </Box>

      {/* Create Dialog */}
      <Dialog
        open={createOpen}
        onClose={closeCreate}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": { maxHeight: "92vh", borderRadius: "12px" },
        }}
      >
        <DialogTitle
          sx={{
            background: "#04343a",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 3,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
            Create Basket
          </Typography>
          <IconButton onClick={closeCreate} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, overflowY: "auto", background: "#eef2f7" }}>
          <BasketForm
            form={createForm}
            setForm={setCreateForm}
            choices={choices}
            isEditing={false}
            submitting={creating}
            onSubmit={handleCreate}
            onClear={() => setCreateForm(emptyForm())}
            onCancel={closeCreate}
          />
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={editOpen}
        onClose={closeEdit}
        maxWidth="lg"
        fullWidth
        sx={{
          "& .MuiDialog-paper": { maxHeight: "92vh", borderRadius: "12px" },
        }}
      >
        <DialogTitle
          sx={{
            background: "#04343a",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: 1.5,
            px: 3,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 16 }}>
            Edit Basket
          </Typography>
          <IconButton onClick={closeEdit} sx={{ color: "#fff" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2, overflowY: "auto", background: "#eef2f7" }}>
          <BasketForm
            form={editForm}
            setForm={setEditForm}
            choices={choices}
            isEditing={true}
            submitting={editing}
            onSubmit={handleEdit}
            onClear={() =>
              setEditForm((prev) => ({
                ...emptyForm(),
                ...prev,
                stocks: [emptyStock()],
              }))
            }
            onCancel={closeEdit}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BasketManagement;
